import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { hasPermission } from "../utils/usePermissions";
import { useNavigate, useLocation } from 'react-router-dom';

const PurposePage = () => {
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [purposeName, setPurposeName] = useState('');
  const [hindiPurposeName, setHindiPurposeName] = useState('');
  const [selectedLedgerGroups, setSelectedLedgerGroups] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const code = localStorage.getItem('code');
  const role = localStorage.getItem('role');
  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const rowData = location?.state?.rowData;
  const index = location?.state?.index
  useEffect(() => {

    if (rowData) {

      handleEdit(rowData, index);
    }
  }, [rowData]);
  const typeOptions = [
    { value: 'Receipt Note', label: 'Receipt Note' },
    { value: 'Delivery Challan', label: 'Delivery Challan' },
    { value: 'Gate Pass', label: 'Gate Pass' },
    { value: 'Security In', label: 'Security In' },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchLedgerGroups(), fetchPurposes()]);
      } catch (err) {
        console.error("Initial data fetch failed", err);
      } finally {
        setLoadingData(false); // ✅ Hide spinner when both are done
      }
    };
    fetchInitialData();
  }, []);

  const fetchLedgerGroups = async () => {
    try {
      if (!token) {
        console.error('Unauthorized: No token found.');
        return;
      }

      // Make the request with the Authorization header
      const response = await apiClient.get(SERVER_BASE + '/ledger-groups', {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`, // Include the JWT token in headers
        },
      });

      // Update the ledger groups state
      setLedgerGroups(response.data);
    } catch (error) {
      console.error('Error fetching ledger groups:', error);
    }
  };

  const fetchPurposes = async () => {
    try {
      // Retrieve the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Unauthorized: No token found.');
        return;
      }
      // Make the request with the Authorization header
      const response = await apiClient.get(SERVER_BASE + '/getpurposes', {
        headers: {
          Authorization: `Bearer ${token}`, // Include the JWT token in headers
        },
      });
      // Update the purposes state
      setPurposes(response.data);
    } catch (error) {
      console.error('Error fetching purposes:', error);
    }
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
  };

  const handleLedgerGroupChange = (selectedOptions) => {
    if (!selectedOptions) {
      setSelectedLedgerGroups([]);
      return;
    }

    const isSelectAll = selectedOptions.some(option => option.value === '__all__');
    const allOptions = ledgerGroups.map(group => ({
      value: group.groupName,
      label: group.groupName,
    }));

    if (isSelectAll) {
      // If "Select All" was clicked, toggle logic
      const alreadyAllSelected = selectedLedgerGroups.length === allOptions.length;
      if (alreadyAllSelected) {
        setSelectedLedgerGroups([]);
      } else {
        setSelectedLedgerGroups(allOptions);
      }
    } else {
      setSelectedLedgerGroups(selectedOptions);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (purposeName.trim() === '' || selectedLedgerGroups.length === 0) {
      Swal.fire('Please fill all the required fields', '', 'warning');
      return;
    }

    // Retrieve token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Unauthorized: No token found.');
      Swal.fire('Unauthorized', 'You need to log in to perform this action.', 'error');
      return;
    }

    const ledgerGroupsNames = selectedLedgerGroups.map((group) => group.label);
    const selectedTypes = selectedType.map(t => t.label).join(', ');

    Swal.fire({
      title: editIndex !== null ? 'Confirm Update' : 'Confirm Create',
      text: editIndex !== null ? 'Are you sure you want to update this purpose?' : 'Are you sure you want to create this purpose?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (editIndex !== null) {
            await apiClient.put(
              `${SERVER_BASE}/updatePurposes/${purposes[editIndex].purpose_id}`,
              {
                name: purposeName,
                hindiPurposeName: hindiPurposeName,
                ledgerGroups: ledgerGroupsNames,
                selectedType: selectedTypes,
                code,
                email
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`, // Add the JWT token in the header
                },
              }
            );
            fetchPurposes();
            setEditIndex(null);
            navigate("../purposeLedger");
          } else {
            // Create new purpose
            await apiClient.post(
              `${SERVER_BASE}/addpurposes`,
              {
                name: purposeName,
                hindiPurposeName: hindiPurposeName,
                ledgerGroups: ledgerGroupsNames,
                code,
                selectedType: selectedTypes,
                email
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`, // Add the JWT token in the header
                },
              }
            );
            navigate("../purposeLedger");
            fetchPurposes();
          }

          resetForm();
          Swal.fire(
            editIndex !== null ? 'Updated!' : 'Created!',
            `Purpose has been ${editIndex !== null ? 'updated' : 'created'}.`,
            'success'
          );
        } catch (error) {
          console.error('Error saving purpose:', error);
          Swal.fire('Error', 'There was an issue saving the purpose. Please try again later.', 'error');
        }
      }
    });
  };

  const resetForm = () => {
    setPurposeName('');
    setHindiPurposeName('')
    setSelectedLedgerGroups([]);
    setSelectedType(null);
  };

  const handleEdit = (rowData, index) => {
    const purpose = rowData;

    setPurposeName(purpose.purpose_name);
    setHindiPurposeName(purpose.hindiPurposeName || '');

    const associatedGroups = (purpose.ledgerGroups || []).map((group) => ({
      value: group.ledger_id,
      label: group.ledger_name,
    }));

    const selectedTypes = (purpose.applicableOn || '')
      .split(',')
      .map((type) => {
        const trimmed = type.trim();
        return { value: trimmed, label: trimmed };
      })
      .filter(item => item.value); // remove empty values if applicableOn was null/empty

    setSelectedType(selectedTypes);
    setSelectedLedgerGroups(associatedGroups);
    setEditIndex(index);
    setShowForm(true);
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Purpose Management - Ledger</h3>
      </div>
      <>
        <button className="btn btn-secondary mb-3" onClick={() => navigate("../purposeLedger")}>
          Cancel
        </button>
        <form onSubmit={handleSubmit} className="formcontainer mb-3">
          <div className="row mb-3 align-items-end">
            <div className="col-md-6">
              <label htmlFor="purposeName" className="form-label">Purpose Name</label>
              <input
                type="text"
                id="purposeName"
                value={purposeName}
                onChange={handleInputChange(setPurposeName)}
                className="form-control"
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="hindiPurposeName" className="form-label">Purpose Name in Hindi</label>
              <input
                type="text"
                id="hindiPurposeName"
                value={hindiPurposeName}
                onChange={handleInputChange(setHindiPurposeName)}
                className="form-control"

              />
            </div>

            <div className="col-md-6">
              <label htmlFor="ledgerGroups" className="form-label">Ledger Groups</label>
              <Select
                isMulti
                options={[
                  { value: '__all__', label: 'Select All' },
                  ...ledgerGroups.map(group => ({ value: group.groupName, label: group.groupName }))
                ]}
                value={selectedLedgerGroups}
                onChange={handleLedgerGroupChange}
                closeMenuOnSelect={false}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="typeSelect" className="form-label">Applicable On</label>
              <Select
                isMulti
                options={typeOptions}
                value={selectedType}
                onChange={setSelectedType}
                placeholder="Select.."
              />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-success">
                {editIndex !== null ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </>

    </div>
  );
};

export default PurposePage;
